import type { Result } from 'true-myth';

import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import {
    type AllowedRoleId,
    type InvitationOptions,
    type InviteUserRequest,
    invitationOptionsSchema,
    type OrganizationUsersParams,
    type PaginatedTenantMembers,
    paginatedTenantMembersSchema,
} from '../../company-settings/api/user-management';

export type UserManagementFetchers = {
    readonly createInvitation: (request: InviteUserRequest) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteInvitation: (invitationId: string) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteUser: (userId: string) => Promise<Result<unknown, HttpFetcherError>>;
    readonly fetchInvitationOptions: () => Promise<Result<InvitationOptions, HttpFetcherError>>;
    readonly fetchOrganizationUsers: (
        params?: OrganizationUsersParams,
    ) => Promise<Result<PaginatedTenantMembers, HttpFetcherError>>;
    readonly resetUserPassword: (userId: string) => Promise<Result<unknown, HttpFetcherError>>;
    readonly updateUserRoles: (
        userId: string,
        roles: readonly AllowedRoleId[],
    ) => Promise<Result<unknown, HttpFetcherError>>;
};

export type UserManagementFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createUserManagementFetchers = (
    dependencies: UserManagementFetchersDependencies,
): UserManagementFetchers => {
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = dependencies;

    const fetchOrganizationUsers: UserManagementFetchers['fetchOrganizationUsers'] = async (params) => {
        const searchParams = new URLSearchParams();

        if (params?.page !== undefined) {
            searchParams.set('page', String(params.page));
        }

        if (params?.size !== undefined) {
            searchParams.set('size', String(params.size));
        }

        if (params?.searchTerm !== undefined && params.searchTerm.trim() !== '') {
            searchParams.set('searchTerm', params.searchTerm.trim());
        }

        if (params?.sortOrder !== undefined) {
            searchParams.set('sortOrder', params.sortOrder);
        }

        const queryString = searchParams.toString();
        const pathname = `/user-management/users${queryString !== '' ? `?${queryString}` : ''}` as const;

        return validatingCobrainerApiFetcher.get(paginatedTenantMembersSchema, {
            pathname,
        });
    };

    const deleteUser: UserManagementFetchers['deleteUser'] = async (userId) => {
        return cobrainerApiFetcher.delete({
            pathname: `/user-management/users/${userId}`,
        });
    };

    const updateUserRoles: UserManagementFetchers['updateUserRoles'] = async (userId, roles) => {
        return cobrainerApiFetcher.patchJson({
            pathname: `/user-management/users/${userId}/roles`,
            payload: { roles },
        });
    };

    const resetUserPassword: UserManagementFetchers['resetUserPassword'] = async (userId) => {
        return cobrainerApiFetcher.post({
            pathname: `/user-management/users/${userId}/reset-password`,
        });
    };

    const createInvitation: UserManagementFetchers['createInvitation'] = async (request) => {
        const { inviteLanguage, ...payload } = request;

        return cobrainerApiFetcher.postJson({
            pathname: '/user-management/invitations',
            payload,
            requestOptions: {
                searchParams: new URLSearchParams(inviteLanguage !== undefined ? { inviteLanguage } : {}),
            },
        });
    };

    const deleteInvitation: UserManagementFetchers['deleteInvitation'] = async (invitationId) => {
        return cobrainerApiFetcher.delete({
            pathname: `/user-management/invitations/${invitationId}`,
        });
    };

    const fetchInvitationOptions: UserManagementFetchers['fetchInvitationOptions'] = async () => {
        return validatingCobrainerApiFetcher.get(invitationOptionsSchema, {
            pathname: '/user-management/invitations/options',
        });
    };

    return {
        createInvitation,
        deleteInvitation,
        deleteUser,
        fetchInvitationOptions,
        fetchOrganizationUsers,
        resetUserPassword,
        updateUserRoles,
    };
};
